import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Header from "../../components/Header";
import api from "../../utils/api";
import { getSocket } from "../../utils/socket";
import { getProfileImageUrl } from "../../utils/imageUtils";
import { usePopup } from "../../context/PopupContext";
import {
  FaSearch,
  FaTimes,
  FaCheckDouble,
  FaCheck,
  FaPaperPlane,
  FaComments,
  FaCheckCircle,
  FaArrowLeft,
  FaEllipsisV,
  FaCircle,
} from "react-icons/fa";
import RoleIllustration from "../../components/RoleIllustration";
import { useTranslation } from "react-i18next";
import "./Messages.css";

const Messages = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showToast } = usePopup();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [mobileShowChat, setMobileShowChat] = useState(false);
  const [typingUser, setTypingUser] = useState(null);

  const lastProcessedUserId = useRef(null);
  const conversationsLoaded = useRef(false);
  const lastQueryParam = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const selectedConvRef = useRef(null);
  const userRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const isTypingRef = useRef(false);

  // Keep refs in sync for use inside socket callbacks
  useEffect(() => {
    selectedConvRef.current = selectedConversation;
  }, [selectedConversation]);
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  // ─── Data fetching ──────────────────────────────────────────
  const fetchConversations = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const response = await api.get("/messages/conversations");
      const data = response.data || [];
      setConversations(
        data.map((conv) => ({
          _id: conv.partner._id || conv.partner,
          otherUser: {
            _id: conv.partner._id || conv.partner,
            name: conv.partner.name || "User",
            role: conv.partner.role || "consumer",
            profileImage: conv.partner.profileImage || null,
            verified: conv.partner.verified || false,
            username: conv.partner.username || null,
            isOnline: conv.partner.isOnline || false,
            lastSeen: conv.partner.lastSeen || null,
          },
          lastMessage: conv.lastMessage?.content || "",
          lastMessageTime: conv.lastMessage?.createdAt || new Date(),
          unreadCount: conv.unreadCount || 0,
        })),
      );
      conversationsLoaded.current = true;
    } catch {
      if (!silent) setConversations([]);
      conversationsLoaded.current = true;
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const fetchUserAndStartConversation = async (userId) => {
    try {
      const res = await api.get(`/users/${userId}`);
      const u = res.data;
      if (u) {
        const conv = {
          _id: u._id,
          otherUser: {
            _id: u._id,
            name: u.name,
            role: u.role,
            profileImage: u.profileImage,
            verified: u.verified,
            username: u.username,
            isOnline: u.isOnline || false,
            lastSeen: u.lastSeen || null,
          },
          lastMessage: "",
          unreadCount: 0,
        };
        setSelectedConversation(conv);
        setMessages([]);
        setMobileShowChat(true);
      }
    } catch {
      /* ignore */
    }
  };

  const fetchMessages = async (otherUserId) => {
    try {
      const res = await api.get(`/messages/conversation/${otherUserId}`);
      setMessages(
        (res.data || []).map((m) => ({
          _id: m._id,
          sender: {
            _id: m.sender._id || m.sender,
            name: m.sender.name || "User",
            role: m.sender.role || "consumer",
            profileImage: m.sender.profileImage || null,
          },
          message: m.content || "",
          timestamp: m.createdAt || new Date(),
          read: m.isRead || false,
        })),
      );
    } catch {
      setMessages([]);
    }
  };

  // ─── Socket.IO setup ───────────────────────────────────────
  useEffect(() => {
    const ud = localStorage.getItem("user");
    if (ud) {
      try {
        const parsed = JSON.parse(ud);
        setUser(parsed);
        userRef.current = parsed;
        fetchConversations();
      } catch {
        setLoading(false);
      }
    } else {
      navigate("/login");
      return;
    }

    const socket = getSocket();
    if (!socket) return;

    const handleNewMessage = (data) => {
      const currentUser = userRef.current;
      const currentConv = selectedConvRef.current;
      if (!currentUser) return;

      const myId = currentUser._id || currentUser.id;
      const senderId = data.sender?._id || data.sender;
      const receiverId = data.receiver?._id || data.receiver;

      const isForMe = receiverId === myId;
      const isMine = senderId === myId;
      if (!isForMe && !isMine) return;

      const partnerId = isMine ? receiverId : senderId;
      const partnerIsSelected =
        currentConv &&
        (currentConv._id === partnerId ||
          currentConv.otherUser?._id === partnerId);

      if (partnerIsSelected) {
        const newMsg = {
          _id: data._id || Date.now().toString(),
          sender:
            typeof data.sender === "object"
              ? data.sender
              : { _id: data.sender },
          message: data.content,
          timestamp: data.createdAt || data.timestamp || new Date(),
          read: data.isRead || false,
        };
        setMessages((prev) => {
          if (prev.some((m) => m._id === newMsg._id)) return prev;
          return [...prev, newMsg];
        });

        if (isForMe) {
          api.put(`/messages/read/${senderId}`).catch(() => {});
        }
      }

      // Update conversation sidebar
      setConversations((prev) => {
        const updated = prev.map((c) => {
          if (c._id === partnerId || c.otherUser?._id === partnerId) {
            return {
              ...c,
              lastMessage: data.content,
              lastMessageTime: data.createdAt || data.timestamp || new Date(),
              unreadCount:
                partnerIsSelected && isForMe
                  ? 0
                  : isForMe
                    ? (c.unreadCount || 0) + 1
                    : c.unreadCount,
            };
          }
          return c;
        });
        const exists = updated.some(
          (c) => c._id === partnerId || c.otherUser?._id === partnerId,
        );
        if (!exists) fetchConversations(true);
        return updated;
      });

      setTypingUser(null);
    };

    const handleMessagesRead = (data) => {
      const currentConv = selectedConvRef.current;
      if (
        currentConv &&
        (currentConv._id === data.readBy ||
          currentConv.otherUser?._id === data.readBy)
      ) {
        setMessages((prev) => prev.map((m) => ({ ...m, read: true })));
      }
    };

    const handleUserOnline = (data) => {
      setConversations((prev) =>
        prev.map((c) =>
          c._id === data.userId || c.otherUser?._id === data.userId
            ? {
                ...c,
                otherUser: {
                  ...c.otherUser,
                  isOnline: true,
                  lastSeen: new Date().toISOString(),
                },
              }
            : c,
        ),
      );
    };

    const handleUserOffline = (data) => {
      setConversations((prev) =>
        prev.map((c) =>
          c._id === data.userId || c.otherUser?._id === data.userId
            ? {
                ...c,
                otherUser: {
                  ...c.otherUser,
                  isOnline: false,
                  lastSeen: new Date().toISOString(),
                },
              }
            : c,
        ),
      );
    };

    const handleUserTyping = (data) => {
      const currentConv = selectedConvRef.current;
      if (
        currentConv &&
        (currentConv._id === data.sender ||
          currentConv.otherUser?._id === data.sender)
      ) {
        if (data.isTyping) {
          setTypingUser(data.sender);
          clearTimeout(typingTimeoutRef.current);
          typingTimeoutRef.current = setTimeout(
            () => setTypingUser(null),
            3000,
          );
        } else {
          setTypingUser(null);
        }
      }
    };

    socket.on("new-message", handleNewMessage);
    socket.on("messages-read", handleMessagesRead);
    socket.on("user-online", handleUserOnline);
    socket.on("user-offline", handleUserOffline);
    socket.on("user-typing", handleUserTyping);

    return () => {
      socket.off("new-message", handleNewMessage);
      socket.off("messages-read", handleMessagesRead);
      socket.off("user-online", handleUserOnline);
      socket.off("user-offline", handleUserOffline);
      socket.off("user-typing", handleUserTyping);
      clearTimeout(typingTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // Handle search params for deep linking
  useEffect(() => {
    const farmerId = searchParams.get("farmer");
    const consumerId = searchParams.get("consumer");
    const userId = farmerId || consumerId;
    if (userId !== lastQueryParam.current) {
      lastProcessedUserId.current = null;
      lastQueryParam.current = userId;
    }
    if (
      userId &&
      conversationsLoaded.current &&
      lastProcessedUserId.current !== userId
    ) {
      const existing = conversations.find(
        (c) => (c.otherUser?._id || c._id)?.toString() === userId.toString(),
      );
      if (existing) {
        setSelectedConversation(existing);
        setMobileShowChat(true);
      } else fetchUserAndStartConversation(userId);
      lastProcessedUserId.current = userId;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, conversations]);

  // Fetch messages & mark read when conversation selected
  useEffect(() => {
    if (selectedConversation) {
      const uid =
        selectedConversation.otherUser?._id || selectedConversation.otherUser;
      if (uid) {
        fetchMessages(uid);
        api
          .put(`/messages/read/${uid}`)
          .then(() => {
            setConversations((prev) =>
              prev.map((c) =>
                c._id === selectedConversation._id
                  ? { ...c, unreadCount: 0 }
                  : c,
              ),
            );
          })
          .catch(() => {});
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConversation]);

  useEffect(scrollToBottom, [messages, scrollToBottom]);

  // ─── Send message ──────────────────────────────────────────
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;

    const receiverId =
      selectedConversation.otherUser._id || selectedConversation.otherUser;
    const msgText = newMessage.trim();
    setNewMessage("");

    // Optimistic local append
    const tempId = `temp_${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      {
        _id: tempId,
        sender: {
          _id: user._id || user.id,
          name: user.name,
          role: user.role,
          profileImage: user.profileImage || null,
        },
        message: msgText,
        timestamp: new Date(),
        read: false,
      },
    ]);

    // Stop typing indicator
    const socket = getSocket();
    if (socket)
      socket.emit("typing", { receiver: receiverId, isTyping: false });
    isTypingRef.current = false;

    try {
      const res = await api.post("/messages", {
        receiver: receiverId,
        content: msgText,
      });
      // Replace temp message with real one
      setMessages((prev) =>
        prev.map((m) =>
          m._id === tempId
            ? {
                _id: res.data._id,
                sender: {
                  _id: user._id || user.id,
                  name: user.name,
                  role: user.role,
                  profileImage: user.profileImage || null,
                },
                message: msgText,
                timestamp: res.data.createdAt || new Date(),
                read: false,
              }
            : m,
        ),
      );
      setConversations((prev) =>
        prev.map((c) =>
          c._id === selectedConversation._id
            ? {
                ...c,
                lastMessage: msgText,
                lastMessageTime: new Date(),
                unreadCount: 0,
              }
            : c,
        ),
      );
    } catch {
      setMessages((prev) => prev.filter((m) => m._id !== tempId));
      showToast(t("messages.sendFailed"), "error");
    }
  };

  // ─── Typing indicator emission ─────────────────────────────
  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
    if (!selectedConversation) return;
    const receiverId =
      selectedConversation.otherUser?._id || selectedConversation.otherUser;
    const socket = getSocket();
    if (!socket) return;

    if (!isTypingRef.current) {
      isTypingRef.current = true;
      socket.emit("typing", { receiver: receiverId, isTyping: true });
    }
    clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      isTypingRef.current = false;
      socket.emit("typing", { receiver: receiverId, isTyping: false });
    }, 2000);
  };

  // ─── Helpers ───────────────────────────────────────────────
  const isUserOnline = (u) => {
    if (!u) return false;
    if (u.lastSeen) return Date.now() - new Date(u.lastSeen).getTime() < 150000;
    return !!u.isOnline;
  };

  const getProfileImage = (u) => {
    if (u?.profileImage)
      return u.profileImage.startsWith("http")
        ? u.profileImage
        : getProfileImageUrl(u.profileImage);
    return "/images/deafultpic.png";
  };

  const formatTime = (date) => {
    const d = new Date(date);
    const diff = Date.now() - d;
    const mins = Math.floor(diff / 60000);
    const hrs = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (mins < 1) return t("messages.justNow");
    if (mins < 60) return t("messages.minutesAgo", { minutes: mins });
    if (hrs < 24)
      return d.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    if (days < 7) return d.toLocaleDateString("en-US", { weekday: "short" });
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const formatMsgTime = (date) =>
    new Date(date).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

  const shouldShowDateSep = (idx) => {
    if (idx === 0) return true;
    return (
      new Date(messages[idx - 1].timestamp).toDateString() !==
      new Date(messages[idx].timestamp).toDateString()
    );
  };

  const formatDateSep = (date) => {
    const d = new Date(date);
    const today = new Date();
    if (d.toDateString() === today.toDateString()) return "Today";
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
    return d.toLocaleDateString("en-US", {
      weekday: "long",
      month: "short",
      day: "numeric",
    });
  };

  const filtered = conversations
    .filter((c) => {
      const sm =
        !searchQuery ||
        c.otherUser.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.lastMessage.toLowerCase().includes(searchQuery.toLowerCase());
      const tm =
        activeTab === "all" || (activeTab === "unread" && c.unreadCount > 0);
      return sm && tm;
    })
    .sort((a, b) => new Date(b.lastMessageTime) - new Date(a.lastMessageTime));

  const totalUnread = conversations.reduce(
    (s, c) => s + (c.unreadCount || 0),
    0,
  );

  const selectConversation = (conv) => {
    setSelectedConversation(conv);
    setMobileShowChat(true);
    setTypingUser(null);
  };

  // ─── Render ────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="msg-page">
        <Header />
        <div className="msg-loading">
          <div className="msg-dots">
            <span />
            <span />
            <span />
          </div>
          <p>{t("messages.loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="msg-page">
      <Header />
      <div className="msg-shell">
        <div
          className={`msg-layout ${mobileShowChat ? "msg-layout--chat-active" : ""}`}
        >
          {/* ===== Sidebar ===== */}
          <aside className="msg-sidebar">
            <div className="msg-sidebar-top">
              <div className="msg-sidebar-head">
                <h2>{t("messages.title")}</h2>
                {totalUnread > 0 && (
                  <span className="msg-unread-total">{totalUnread}</span>
                )}
              </div>
              <button
                className="msg-icon-btn"
                onClick={() => setSearchOpen(!searchOpen)}
              >
                {searchOpen ? <FaTimes /> : <FaSearch />}
              </button>
            </div>

            {searchOpen && (
              <div className="msg-search-wrap">
                <FaSearch className="msg-search-ico" />
                <input
                  autoFocus
                  type="text"
                  placeholder={t("messages.searchPlaceholder")}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button
                    className="msg-search-clear"
                    onClick={() => setSearchQuery("")}
                  >
                    <FaTimes />
                  </button>
                )}
              </div>
            )}

            <div className="msg-tabs">
              <button
                className={`msg-tab ${activeTab === "all" ? "msg-tab--on" : ""}`}
                onClick={() => setActiveTab("all")}
              >
                {t("messages.allMessages")}
              </button>
              <button
                className={`msg-tab ${activeTab === "unread" ? "msg-tab--on" : ""}`}
                onClick={() => setActiveTab("unread")}
              >
                {t("messages.unread")}
                {totalUnread > 0 && (
                  <span className="msg-tab-badge">{totalUnread}</span>
                )}
              </button>
            </div>

            <div className="msg-convos">
              {filtered.length === 0 ? (
                <div className="msg-convos-empty">
                  <FaComments />
                  <p>{t("messages.noConversations")}</p>
                </div>
              ) : (
                filtered.map((c) => (
                  <div
                    key={c._id}
                    className={`msg-convo ${selectedConversation?._id === c._id ? "msg-convo--on" : ""} ${c.unreadCount > 0 ? "msg-convo--unread" : ""}`}
                    onClick={() => selectConversation(c)}
                  >
                    <div className="msg-convo-avatar-wrap">
                      <img
                        src={getProfileImage(c.otherUser)}
                        alt=""
                        className="msg-convo-avatar"
                        referrerPolicy="no-referrer"
                      />
                      {isUserOnline(c.otherUser) && (
                        <span className="msg-convo-online" />
                      )}
                    </div>
                    <div className="msg-convo-body">
                      <div className="msg-convo-top">
                        <span className="msg-convo-name">
                          {c.otherUser.name}
                          {c.otherUser.verified && (
                            <FaCheckCircle className="msg-convo-verified" />
                          )}
                        </span>
                        <span className="msg-convo-time">
                          {formatTime(c.lastMessageTime)}
                        </span>
                      </div>
                      <div className="msg-convo-bottom">
                        <span className="msg-convo-preview">
                          {c.lastMessage || t("messages.noMessages")}
                        </span>
                        {c.unreadCount > 0 && (
                          <span className="msg-convo-badge">
                            {c.unreadCount}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </aside>

          {/* ===== Chat ===== */}
          <main className="msg-chat">
            {!selectedConversation ? (
              <div className="msg-chat-empty">
                <div className="msg-chat-empty-icon">
                  <FaComments />
                </div>
                <h2>{t("messages.noSelection")}</h2>
                <p>{t("messages.noSelectionHint")}</p>
              </div>
            ) : (
              <>
                <div className="msg-chat-head">
                  <button
                    className="msg-back-btn"
                    onClick={() => setMobileShowChat(false)}
                  >
                    <FaArrowLeft />
                  </button>
                  <Link
                    to={`/users/${selectedConversation.otherUser.username || selectedConversation.otherUser._id}`}
                    className="msg-chat-user"
                  >
                    <div className="msg-chat-avatar-wrap">
                      <img
                        src={getProfileImage(selectedConversation.otherUser)}
                        alt=""
                        className="msg-chat-avatar"
                        referrerPolicy="no-referrer"
                      />
                    </div>
                    <div className="msg-chat-user-meta">
                      <h3>
                        {selectedConversation.otherUser.name}
                        {selectedConversation.otherUser.verified && (
                          <FaCheckCircle className="msg-chat-verified" />
                        )}
                        {isUserOnline(selectedConversation.otherUser) ? (
                          <span className="msg-online-tag">
                            <FaCircle /> Online
                          </span>
                        ) : (
                          <span className="msg-offline-tag">
                            <FaCircle /> Offline
                          </span>
                        )}
                      </h3>
                      <span className="msg-chat-role">
                        {selectedConversation.otherUser.role === "farmer" ? (
                          <>
                            <RoleIllustration role="farmer" size="xs" /> Farmer
                          </>
                        ) : (
                          <>
                            <RoleIllustration role="consumer" size="xs" />{" "}
                            Consumer
                          </>
                        )}
                      </span>
                    </div>
                  </Link>
                  <button className="msg-icon-btn">
                    <FaEllipsisV />
                  </button>
                </div>

                <div className="msg-messages">
                  {messages.length === 0 && !typingUser && (
                    <div className="msg-messages-empty">
                      <FaPaperPlane />
                      <p>Send a message to start the conversation</p>
                    </div>
                  )}
                  {messages.map((m, i) => {
                    const own = m.sender._id === (user._id || user.id);
                    return (
                      <React.Fragment key={m._id}>
                        {shouldShowDateSep(i) && (
                          <div className="msg-date-sep">
                            <span>{formatDateSep(m.timestamp)}</span>
                          </div>
                        )}
                        <div
                          className={`msg-bubble ${own ? "msg-bubble--own" : "msg-bubble--other"}`}
                        >
                          {!own && (
                            <img
                              src={getProfileImage(m.sender)}
                              alt=""
                              className="msg-bubble-avatar"
                              referrerPolicy="no-referrer"
                            />
                          )}
                          <div className="msg-bubble-inner">
                            <div className="msg-bubble-text">{m.message}</div>
                            <span className="msg-bubble-time">
                              {formatMsgTime(m.timestamp)}
                              {own &&
                                (m.read ? (
                                  <FaCheckDouble className="msg-read" />
                                ) : (
                                  <FaCheck className="msg-sent" />
                                ))}
                            </span>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                  {typingUser && (
                    <div className="msg-bubble msg-bubble--other">
                      <img
                        src={getProfileImage(selectedConversation.otherUser)}
                        alt=""
                        className="msg-bubble-avatar"
                        referrerPolicy="no-referrer"
                      />
                      <div className="msg-bubble-inner msg-typing-bubble">
                        <div className="msg-typing-dots">
                          <span />
                          <span />
                          <span />
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                <form onSubmit={handleSendMessage} className="msg-input-bar">
                  <input
                    ref={inputRef}
                    type="text"
                    placeholder={t("messages.typePlaceholder")}
                    value={newMessage}
                    onChange={handleInputChange}
                  />
                  <button
                    type="submit"
                    disabled={!newMessage.trim()}
                    className="msg-send-btn"
                  >
                    <FaPaperPlane />
                  </button>
                </form>
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default Messages;
